import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Newsletter from '../components/Newsletter';
import Footer from '../components/Footer';

interface FAQItem {
  question: string;
  answer: string;
}

const LavageVelo: React.FC = () => {
  const [openFAQ, setOpenFAQ] = useState<number | null>(null);

  const forfaits = [
    {
      id: 'or',
      nom: 'Forfait Or',
      sousTitre: 'Nettoyage complet & finition',
      prix: '45€',
      image: '/lavage/fe5b4dbd567cfb5a627c191ee642fcb5.jpg',
      description: "Nettoyage intégral du cadre, fourche et roues, dégraissage complet de la transmission, nettoyage des freins et lubrification de la chaîne. Contrôle visuel gratuit (jeux de direction, patins/plaquettes, tension chaîne). Idéal pour préparation de course, après sorties boueuses ou fin de saison."
    },
    {
      id: 'argent',
      nom: 'Forfait Argent',
      sousTitre: 'Nettoyage approfondi',
      prix: '30€',
      image: '/lavage/6baa0e222c53925f1d4f02a97f857cbc.jpg',
      description: "Nettoyage et dégraissage ciblé de la transmission, rinçage soigné du cadre et des roues, contrôle rapide des éléments mécaniques majeurs. Lubrification standard de la chaîne. Bon équilibre pour entretien régulier."
    },
    {
      id: 'bronze',
      nom: 'Forfait Bronze',
      sousTitre: 'Lavage express',
      prix: '15€',
      image: '/lavage/8ac0498e22021a140ef30d7f9d5de57b.jpg',
      description: "Lavage extérieur du cadre et des roues, rinçage et séchage. Intervention rapide pour garder le vélo propre entre deux entretiens. Convient pour un nettoyage rapide après sorties sur routes propres."
    }
  ];

  const faqItems: FAQItem[] = [
    {
      question: "Combien de temps dure un lavage selon le forfait choisi ?",
      answer: "Le temps varie de 10 minutes (Bronze) à 30-45 minutes (Or). Nous vous indiquons un délai précis à la prise de rendez-vous."
    },
    {
      question: "Le lavage peut-il être combiné avec une révision ?",
      answer: "Oui, et c'est même recommandé : un vélo propre permet une inspection plus précise avant révision."
    },
    {
      question: "Utilisez-vous des produits compatibles avec les cadres carbone ?",
      answer: "Oui, nous utilisons uniquement des produits adaptés aux peintures, vernis et matériaux haut de gamme (carbone, titane, aluminium)."
    },
    {
      question: "Le dégraissage de la transmission est-il compris dans tous les forfaits ?",
      answer: "Le dégraissage complet est inclus dans le forfait OR, et partiel dans le forfait ARGENT. Le forfait BRONZE est un lavage express sans dégraissage."
    },
    {
      question: "Proposez-vous un séchage soufflé pour éviter les résidus d'eau ?",
      answer: "Oui, un séchage soufflé et un essuyage précis sont réalisés sur tous les forfaits."
    },
    {
      question: "Le lavage peut-il enlever du bruit ou des craquements ?",
      answer: "Le lavage peut révéler la cause d'un bruit, mais si une réparation est nécessaire, un devis vous sera proposé."
    },
    {
      question: "Est-ce que vous nettoyez aussi les cassettes et plateaux ?",
      answer: "Oui, dans le cadre du forfait OR, la transmission est nettoyée en profondeur."
    },
    {
      question: "Puis-je laisser mon vélo pour lavage et le récupérer plus tard ?",
      answer: "Oui, vous pouvez déposer le vélo et le récupérer dans la même journée selon le planning de l'atelier."
    },
    {
      question: "Faites-vous un nettoyage intérieur des gaines ou durites ?",
      answer: "Non, cela relève d'une révision mécanique approfondie, pas d'un lavage."
    },
    {
      question: "Quel type de vélos acceptez-vous ?",
      answer: "Nous prenons uniquement les vélos de route, triathlon et gravel. Les VAE et vélos de ville ne sont pas acceptés."
    }
  ];

  const toggleFAQ = (index: number) => {
    setOpenFAQ(openFAQ === index ? null : index);
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero Section */}
      <section className="pt-24 md:pt-32 pb-12 md:pb-0">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-center text-black mb-12 leading-tight">
            Lavage vélo route, triathlon et gravel à<br />
            Paris
          </h1>

          {/* Main Image */}
          <div className="relative rounded-lg overflow-hidden mb-16 max-w-4xl mx-auto">
            <img
              src="/lavage/bb21c37c60d3449876acc1fc99c6a1fa.jpg"
              alt="Lavage vélo professionnel"
              className="w-full h-auto object-cover"
            />
          </div>
        </div>
      </section>

      {/* Content Section with Sidebar */}
      <section className="py-12 md:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-8">
              <div>
                <h2 className="text-2xl font-bold text-black mb-4">
                  Un service de lavage professionnel pour votre vélo
                </h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Forfaits <strong>Or</strong>, <strong>Argent</strong> et <strong>Bronze</strong> — nettoyage adapté pour vélos de route, triathlon et gravel. Vélos de ville et vélos électriques non acceptés.
                </p>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Il est parfois difficile de trouver le moyen de laver son vélo en habitant à Paris ou en petite couronne. <strong>Podium vous propose ses solutions de lavage</strong> pour bichonner votre vélo avec des produits adaptés aux cadres carbone et composants haut de gamme.
                </p>
              </div>

              <div>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Que ce soit pour une préparation de course, après des sorties boueuses ou en fin de saison, notre équipe prend soin de chaque détail : nettoyage du cadre, dégraissage de la transmission, lubrification et contrôle visuel.
                </p>
                <p className="text-gray-700 leading-relaxed">
                  Réservez votre créneau de lavage et repartez avec un vélo comme neuf, prêt pour vos prochaines aventures.
                </p>
              </div>

              {/* CTA Button */}
              <div className="pt-4">
                <Link
                  to="/reservation-lavage"
                  className="inline-block bg-black text-white px-8 py-4 rounded-lg hover:bg-gray-800 transition-colors duration-300 font-medium text-lg"
                >
                  Je réserve mon lavage vélo
                </Link>
              </div>
            </div>

            {/* Sidebar - Les Infos Essentielles */}
            <div className="lg:col-span-1">
              <div className="bg-gray-50 rounded-lg p-6 sticky top-24">
                <h3 className="text-xl font-bold text-black mb-6">Les Infos Essentielles</h3>
                <ul className="space-y-4 text-gray-700 text-sm">
                  <li className="flex items-start">
                    <span className="mr-2">📍</span>
                    <span><strong>Lieu :</strong> 26 Bd Henri Sellier, 92150 Suresnes</span>
                  </li>
                  <li className="flex items-start">
                    <span className="mr-2">⏱️</span>
                    <span><strong>Durée :</strong> De 10 à 45 min selon forfait</span>
                  </li>
                  <li className="flex items-start">
                    <span className="mr-2">💰</span>
                    <span><strong>Prix :</strong> De 15€ (Bronze) à 45€ (Or)</span>
                  </li>
                  <li className="flex items-start">
                    <span className="mr-2">🚴</span>
                    <span><strong>Vélos acceptés :</strong> Route, Triathlon, Gravel uniquement</span>
                  </li>
                  <li className="flex items-start">
                    <span className="mr-2">✨</span>
                    <span><strong>Produits :</strong> Adaptés aux cadres carbone et composants haut de gamme</span>
                  </li>
                  <li className="flex items-start">
                    <span className="mr-2">📞</span>
                    <span><strong>Contact :</strong> 01 XX XX XX XX</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Nos forfaits - Section avec grandes cartes */}
      <section className="py-12 md:py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center text-black mb-4">Nos forfaits</h2>
          <p className="text-center text-gray-600 mb-12">Choisissez le forfait adapté à vos besoins</p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {forfaits.map((forfait) => (
              <div key={forfait.id} className="bg-white rounded-2xl overflow-hidden shadow-sm">
                <div className="h-48 overflow-hidden">
                  <img
                    src={forfait.image}
                    alt={forfait.nom}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="p-6 text-center">
                  <h3 className="font-bold text-xl mb-1">{forfait.nom}</h3>
                  <p className="text-gray-500 text-sm mb-3">{forfait.sousTitre}</p>
                  <p className="text-3xl font-bold mb-4">{forfait.prix}</p>
                  <p className="text-gray-600 text-sm leading-relaxed text-left">
                    {forfait.description}
                  </p>
                </div>
              </div>
            ))}
          </div>

          {/* CTA Button */}
          <div className="text-center mt-12">
            <Link
              to="/reservation-lavage"
              className="inline-block bg-black text-white px-8 py-4 rounded-lg hover:bg-gray-800 transition-colors duration-300 font-medium text-lg"
            >
              Je réserve mon lavage vélo
            </Link>
          </div>
        </div>
      </section>

      {/* Pourquoi choisir Podium */}
      <section className="py-12 md:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-black mb-6">
            Pourquoi faire laver son vélo chez Podium ?
          </h2>
          <ul className="space-y-4 text-gray-700 max-w-3xl">
            <li className="flex items-start gap-3">
              <div className="w-8 h-8 bg-black rounded-full flex items-center justify-center flex-shrink-0">
                <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <span><strong>Produits professionnels</strong> – Adaptés aux cadres carbone et composants haut de gamme</span>
            </li>
            <li className="flex items-start gap-3">
              <div className="w-8 h-8 bg-black rounded-full flex items-center justify-center flex-shrink-0">
                <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <span><strong>Service rapide</strong> – De 10 à 45 minutes selon le forfait</span>
            </li>
            <li className="flex items-start gap-3">
              <div className="w-8 h-8 bg-black rounded-full flex items-center justify-center flex-shrink-0">
                <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <span><strong>Contrôle visuel gratuit</strong> – Vérification des éléments essentiels</span>
            </li>
            <li className="flex items-start gap-3">
              <div className="w-8 h-8 bg-black rounded-full flex items-center justify-center flex-shrink-0">
                <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <span><strong>Idéalement situé</strong> – À 10 min de Paris, facile d'accès</span>
            </li>
            <li className="flex items-start gap-3">
              <div className="w-8 h-8 bg-black rounded-full flex items-center justify-center flex-shrink-0">
                <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <span><strong>Expertise cycliste</strong> – Une équipe passionnée à votre service</span>
            </li>
          </ul>
        </div>
      </section>

      {/* Images Section */}
      <section className="py-12 md:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="rounded-lg overflow-hidden">
              <img
                src="/lavage/ac7af327336ce2faf4be1e6c6084e16b.jpg"
                alt="Lavage vélo - Atelier"
                className="w-full h-64 md:h-80 object-cover"
              />
            </div>
            <div className="rounded-lg overflow-hidden">
              <img
                src="/lavage/af9b0c6f7beaecbf9433101d2a28baa1.jpg"
                alt="Lavage vélo - Résultat"
                className="w-full h-64 md:h-80 object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Confiance Section - Avis Clients */}
      <section className="py-12 md:py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-bold text-black mb-4">
              Ils ont fait confiance à Podium pour le lavage de leur vélo
            </h2>
            <div className="flex items-center justify-center gap-2 mb-2">
              <div className="flex">
                {[1, 2, 3, 4, 5].map((star) => (
                  <svg key={star} className="w-6 h-6 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                  </svg>
                ))}
              </div>
              <span className="text-xl font-bold text-black">4.9</span>
              <span className="text-gray-500">sur Google</span>
            </div>
          </div>

          {/* Avis Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Avis 1 */}
            <div className="bg-white rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center text-white font-bold text-lg">
                  T
                </div>
                <div>
                  <h4 className="font-semibold text-black">Thomas D.</h4>
                  <div className="flex items-center gap-1">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <svg key={star} className="w-4 h-4 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                      </svg>
                    ))}
                    <span className="text-xs text-gray-400 ml-2">il y a 2 semaines</span>
                  </div>
                </div>
              </div>
              <p className="text-gray-700 text-sm leading-relaxed">
                "Service impeccable ! Mon vélo est revenu comme neuf. L'équipe est très professionnelle et prend vraiment soin des détails. Je recommande vivement le forfait Or."
              </p>
            </div>

            {/* Avis 2 */}
            <div className="bg-white rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-gradient-to-br from-rose-500 to-rose-600 rounded-full flex items-center justify-center text-white font-bold text-lg">
                  S
                </div>
                <div>
                  <h4 className="font-semibold text-black">Sophie M.</h4>
                  <div className="flex items-center gap-1">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <svg key={star} className="w-4 h-4 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                      </svg>
                    ))}
                    <span className="text-xs text-gray-400 ml-2">il y a 1 mois</span>
                  </div>
                </div>
              </div>
              <p className="text-gray-700 text-sm leading-relaxed">
                "Excellent travail de nettoyage ! La transmission est parfaitement dégraissée et la chaîne bien lubrifiée. Prix très corrects pour la qualité du service."
              </p>
            </div>

            {/* Avis 3 */}
            <div className="bg-white rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-full flex items-center justify-center text-white font-bold text-lg">
                  P
                </div>
                <div>
                  <h4 className="font-semibold text-black">Pierre L.</h4>
                  <div className="flex items-center gap-1">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <svg key={star} className="w-4 h-4 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                      </svg>
                    ))}
                    <span className="text-xs text-gray-400 ml-2">il y a 3 semaines</span>
                  </div>
                </div>
              </div>
              <p className="text-gray-700 text-sm leading-relaxed">
                "J'ai testé le forfait Argent et je suis très satisfait. Mon vélo brille de mille feux ! Service rapide et efficace, je reviendrai sans hésiter."
              </p>
            </div>
          </div>

          {/* Google Badge */}
          <div className="mt-10 flex justify-center">
            <button
              onClick={() => window.open('https://www.google.com/maps', '_blank')}
              className="inline-flex items-center gap-2 bg-white px-6 py-3 rounded-full shadow-sm hover:shadow-md transition-all text-gray-700 font-medium cursor-pointer"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
              </svg>
              Voir tous nos avis sur Google
            </button>
          </div>
        </div>
      </section>

      {/* Conditions & exclusions */}
      <section className="py-12 md:py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold mb-6 text-center">Conditions & exclusions</h2>
          <p className="text-gray-700 leading-relaxed mb-8 text-center">
            Nous n'acceptons pas les vélos électriques (VAE) ni les vélos de ville. Le lavage n'inclut pas systématiquement le démontage complet du vélo ou la révision mécanique — pour cela, demandez-nous une révision en atelier.
          </p>

          {/* CTA Button */}
          <div className="text-center">
            <Link
              to="/reservation-lavage"
              className="inline-block bg-black text-white px-8 py-4 rounded-lg hover:bg-gray-800 transition-colors duration-300 font-medium text-lg"
            >
              Je réserve mon lavage vélo
            </Link>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-12 md:py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center text-black mb-12">FAQ</h2>

          <div className="space-y-4">
            {faqItems.map((item, index) => (
              <div key={index} className="border-b border-gray-200 pb-4">
                <button
                  onClick={() => toggleFAQ(index)}
                  className="w-full text-left flex justify-between items-center py-2 hover:text-gray-600 transition-colors"
                >
                  <span className="font-medium text-black pr-4">{item.question}</span>
                  <svg
                    className={`w-5 h-5 flex-shrink-0 transform transition-transform ${openFAQ === index ? 'rotate-180' : ''}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                {openFAQ === index && (
                  <p className="text-gray-600 mt-2 text-sm leading-relaxed">
                    {item.answer}
                  </p>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      <Newsletter />
      <Footer />
    </div>
  );
};

export default LavageVelo;
